import React, { useEffect, useRef } from "react";
import { useSnackbar } from "notistack";
import {
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  List,
  Box,
  Collapse,
  Button,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { getSchema } from "../helpers/schemas/getSchema";
import { getFormInvalid } from "../helpers/getFormInvalid";
import { checkInvalidField } from "../helpers/checkInvalidField";
import ImportDialog from "../Dialogs/ImportDialog";
import EditDialog from "../Dialogs/EditDialog";
import AddBoxIcon from "@mui/icons-material/AddBox";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CommentCard from "./CommentsCard";
import { applyEditTrackingFields } from "../helpers/applyEditTrackingFields";
import { getExcelFeatures } from "../helpers/getExcelFeatures";
import SortDialog from "../Dialogs/SortDialog";
import { getSortArray } from "../helpers/getSortArray";
import setupEdit from "../helpers/setupEdit";
import { getCounts } from "../helpers/getCounts";
import { processFiles } from "../helpers/processFiles";
import { onEditSaveAll } from "../helpers/onEditSaveAll";
import { onEditDeleteAll } from "../helpers/onEditDeleteAll";
import { VisuallyHiddenInput } from "../helpers/VisuallyHiddenInput";
import { getUserAccess } from "../helpers/getUserAccess";
import { Unpublished } from "@mui/icons-material";
import { getEditorTrackingFieldNames } from "../helpers/schemas/getEditorTrackingFields";

export default function CommentsList(props) {
  const fileInputRef = useRef(null);
  const fileInputRef1 = useRef(null);
  const navigate = useNavigate();
  // console.log(location.state);
  const { enqueueSnackbar } = useSnackbar();
  const [importDialogOpen, setImportDialogOpen] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);

  const [project, setProject] = React.useState({});
  const [allFeatures, setAllFeatures] = React.useState([]);
  const [features, setFeatures] = React.useState([]);
  const [sortByValue, setSortByValue] = React.useState("No");
  const [sortByOptions] = React.useState(["No", "No Desc"]);
  const [importTitle, setImportTitle] = React.useState(
    "Comments Import > Choose Worksheet",
  );
  const [importStats, setImportStats] = React.useState([]);
  const [importSubtitle, setImportSubtitle] = React.useState(
    `Choose the worksheet with the data you wish to import.`,
  );
  const [importData, setImportData] = React.useState([]);
  const [importFields, setImportFields] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [filterValue, setFilterValue] = React.useState("");
  const [editFeature, setEditFeature] = React.useState(null);
  const [editFiles, setEditFiles] = React.useState([]);
  const [editSchema, setEditSchema] = React.useState([]);
  const [formErrorMessage, setformErrorMessage] = React.useState("");
  const [sheetName, setSheetName] = React.useState(null);
  const [workbook, setWorkBook] = React.useState({ SheetNames: [] });
  const [isLoading, setIsLoading] = React.useState(true);
  const [isReady, setIsReady] = React.useState(false);

  useEffect(() => {
    const fetchData = () => {
      var isus = props.project.Comments;
      isus = getCounts(
        isus,
        props.project.Files,
        [],
        props.project.Comments,
        "comment_id",
      );
      setAllTheComments(isus);
      setProject(props.project);
      setEditSchema(getSchema("comment"));
      setIsLoading(false);
    };
    if (editSchema.length === 0 && isReady === false) {
      setIsReady(true);
      fetchData();
    }
  });

  const setAllTheComments = (iss) => {
    var reslts;
    if (props.observationId !== null) {
      reslts = iss.filter((i) => i.observation_id === props.observationId);
    } else if (props.equipmentId !== null) {
      reslts = iss.filter((i) => i.equipment_id === props.equipmentId);
    } else if (props.issueId !== null) {
      reslts = iss.filter((i) => i.issue_id === props.issueId);
    } else {
      reslts = iss;
    }
    setAllFeatures([...reslts]);
    onSortChange(sortByValue, [...reslts]);
  };

  const onSortChange = (value, array) => {
    if (value !== sortByValue) {
      setSortByValue(value);
    }
    const obs = getSortArray(value, array);
    setFeatures([...obs]);
  };

  const getAcceptedFiles = async (files) => {
    try {
      const readyFiles = await processFiles(
        files,
        "comment_id",
        editFeature.id,
      );
      const allFiles = editFiles.concat(readyFiles);
      setEditFiles([...allFiles]);
    } catch (error) {
      console.error("Error getting document:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
        variant: "error",
      });
    }
  };

  const onEditFileCommentChange = (e) => {
    var file = editFiles.find((f) => f.id === e.target.id);
    file.comment = e.target.value;
    setEditFiles([...editFiles]);
  };

  const onRemoveEditFile = async (id) => {
    const updatedFiles = editFiles.map((obj) => {
      if (obj.id === id) {
        obj.deleted = true;
        return obj; // Replace the object if its ID matches
      }
      return obj; // Keep other objects as they are
    });

    setEditFiles([...updatedFiles]);
  };

  const onAddComment = () => {
    var item = {};
    var schema = getSchema("comment");
    schema.forEach((fd) => {
      item[fd.name] = "";
    });

    item["id"] = "new";
    item["project_id"] = project["id"];
    item["observation_id"] =
      props.observationId !== null ? props.observationId : "";
    item["issue_id"] = props.issueId !== null ? props.issueId : "";
    item["equipment_id"] = props.equipmentId !== null ? props.equipmentId : "";
    item = applyEditTrackingFields(item, props.user);

    setEditSchema(schema);
    setEditFeature(item);
    setEditFiles([]);
    setImportTitle("New Comment");
    setEditDialogOpen(true);
  };

  const onEdit = (id) => {
    var item = features.find((f) => f.id === id);
    item = { ...item };
    const results = setupEdit(item, "comment");
    setEditSchema(results.schema);
    setEditFeature({ ...results.item });
    const files = project.Files.filter((p) => p.comment_id === id);
    setEditFiles([...files]);
    setImportTitle(`Edit Comment (No:${item.No})`);
    setEditDialogOpen(true);
  };

  const onEditDelete = async () => {
    try {
      setIsLoading(true);
      const savedProj = await onEditDeleteAll(
        project,
        editFeature,
        editFiles,
        "Comments",
        "comment_id",
        props.user,
      );

      // props.saveCurrentProject({ ...savedProj });
      setProject({ ...savedProj });
      setAllTheComments(savedProj.Comments);
      enqueueSnackbar("Comment deleted successfully", {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "success",
      });
      setEditDialogOpen(false);
      setIsLoading(false);
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
      setIsLoading(false);
    }
  };

  const onEditSave = async () => {
    try {
      //check for invalid
      var feat = { ...editFeature };
      var invalidResults = getFormInvalid(feat, editSchema);
      if (invalidResults.isInvalid === true) {
        setEditFeature({ ...invalidResults.feature });
        setEditSchema([...invalidResults.schema]);
        setformErrorMessage(invalidResults.formErrorMessage);
        return;
      }

      setIsLoading(true);

      const savedProj = await onEditSaveAll(
        project,
        feat,
        editSchema,
        editFiles,
        "Comments",
        "comment_id",
        props.user,
      );

      // props.passUpProject(savedProj);
      // props.passIsLoading(false);
      // props.saveCurrentProject({ ...savedProj });
      setProject({ ...savedProj });
      setAllTheComments(savedProj.Comments);
      setEditDialogOpen(false);
      enqueueSnackbar("Comment saved successfully", {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "success",
      });
      setIsLoading(false);
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
      setIsLoading(false);
    }
  };

  const onEditChange = (e) => {
    try {
      // console.log(e);
      var att = editSchema.find((item) => item.name === e.target.name);
      //clearout erro message field
      const formErrorMessage = checkInvalidField(e.target.value, att);
      //format dates
      if (att.type === "date") {
        if (e.target.value.length > 0) {
          var date = new Date(e.target.value);
          // date.setDate(date.getDate() + 1);
          editFeature[e.target.name] = date.toISOString().split("T")[0];
          // editFeature[e.target.name] = date.getTime();
          setEditFeature({ ...editFeature });
          setformErrorMessage(formErrorMessage);
        }
      } else if (att.type === "bit") {
        editFeature[e.target.name] = e.target.checked;
        setEditFeature({ ...editFeature });
        setformErrorMessage(formErrorMessage);
      } else {
        editFeature[e.target.name] = e.target.value;
        setEditFeature({ ...editFeature });
        setformErrorMessage(formErrorMessage);
      }
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
    }
  };

  const onEditCancel = () => {
    setEditDialogOpen(false);
  };

  const onDetailsClick = (item) => {
    if (item.focus.length > 0) {
      navigate(
        `/project/${project.id}/comment/${item.id}?focus=${item.focus}`,
        {
          state: { passProject: project },
        },
      );
    } else {
      navigate(`/project/${project.id}/comment/${item.id}`, {
        state: { passProject: project },
      });
    }
  };

  const handleChangePage = (event, newPage) => {
    // console.log(newPage);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onFilterChange = (searchTerm) => {
    if (searchTerm.length > 2) {
      const filteredData = allFeatures.filter((obj) => {
        const values = Object.values(obj);
        return values.some((value) =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase()),
        );
      });
      setFeatures([...filteredData]);
    } else {
      setFeatures([...allFeatures]);
    }
    setFilterValue(searchTerm);
    setPage(0);
  };

  const handleChecked = (e) => {
    const item = features.find((item) => item.id === e.target.id);
    item.Selected = !item.Selected;
    setFeatures([...features]);
  };

  const onCloseImportDialog = () => {
    setImportDialogOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
    if (fileInputRef1.current) {
      fileInputRef1.current.value = null;
    }
    setSheetName(null);
    setImportTitle("Comments Import > Choose Worksheet");
    setImportSubtitle(`Choose the worksheet with the data you wish to import.`);
  };

  const onAttributeChange = (e) => {
    var fd = importFields.find((f) => f.name === e.target.name);
    fd.attribute = e.target.value;
    setImportFields([...importFields]);
  };

  const onFldChange = (e) => {
    var fd = importFields.find((f) => f.name === e.target.name);
    fd.selected = e.target.value;
    setImportFields([...importFields]);
  };

  const processExcel = (file) => {
    try {
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: "array" });

          setWorkBook(workbook);
          setImportDialogOpen(true);
          setImportTitle("Choose a worksheet");
        };
        reader.readAsArrayBuffer(file);
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
    }
  };

  const onWorksheetSelected = async (e) => {
    try {
      // Assuming you want to read the first sheet
      const worksheet = workbook.Sheets[e];

      // Convert sheet data to an array of objects or an array of arrays
      const jsonData = XLSX.utils.sheet_to_json(worksheet); // Array of objects
      // const arrayOfArrays = XLSX.utils.sheet_to_array(worksheet); // Array of arrays

      console.log(jsonData); // Or update your component state with this data
      if (jsonData.length > 0) {
        var fields = [];
        Object.keys(jsonData[0]).forEach((colName) => {
          var match = editSchema.find((f) => f.name === colName);
          if (match === undefined) {
            editSchema.forEach((fd) => {
              if (
                fd.hasOwnProperty("similair") &&
                fd.similair.includes(colName.toLowerCase())
              )
                match = fd;
            });
          }

          var availableFds = editSchema
            .filter(
              (f) =>
                f.name !== "id" &&
                !f.name.includes("_id") &&
                getEditorTrackingFieldNames().includes(f.name) === false &&
                f.name !== "fileCount" &&
                f.name !== "commentCount" &&
                f.name !== "No",
            )
            .map((f) => f.name);

          availableFds.push("New Attribute");

          fields.push({
            name: colName,
            selected: match ? match.name : "New Attribute",
            options: availableFds,
            attribute: colName,
            error: false,
          });
        });
        setImportData(jsonData);
        setSheetName(e);
        setImportFields(fields);
        setImportTitle("Comments Import > Choose Fields");
        setImportSubtitle(
          `For each column of your spreadsheet, select the corresponding field. The "Comment Description" field is required.`,
        );
      } else {
        throw new Error("Unable to import comments");
      }
    } catch (err) {
      throw new Error("Unable to import comments: " + err.message);
    }
  };

  const onImportExcel = async () => {
    try {
      if (importData.length === 0) {
        onCloseImportDialog();
        return;
      }

      const results = await getExcelFeatures(
        props.user,
        importData,
        importFields,
        editSchema,
        ["Comment Description"],
        project,
        "Comments",
        props.observationId !== null
          ? "observation_id"
          : props.issueId !== null
            ? "issue_id"
            : props.equipmentId !== null
              ? "equipment_id"
              : null,
        props.observationId !== null
          ? props.observationId
          : props.issueId !== null
            ? props.issueId
            : props.equipmentId !== null
              ? props.equipmentId
              : null,
      );

      setImportStats([...getStats(results.newEquip)]);
      setImportSubtitle("Import Successful: Results shown below");
      setImportTitle("Comments Import > Complete");
      setImportFields([]);
      setFilterValue("");
      setPage(0);
      setProject({ ...results.project });
      setAllTheComments(results.freshFeatures);
      setImportData([]);
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
      // onCloseImportDialog();
    }
  };

  const getStats = (array) => {
    var importStat = [];
    importStat.push({ name: "Comments", count: array.length });
    return importStat;
  };

  const onUncheckSelected = () => {
    features.forEach((item) => {
      if (item.Selected === true) {
        item.Selected = false;
      }
    });
    setFeatures([...features]);
  };

  const onDrawComplete = (id, pic) => {
    var file = editFiles.find((f) => f.id === id);
    if (file) {
      file.file = pic;
      if (!file.id.startsWith("new_")) {
        file.isDrawn = true;
      }
      setEditFiles([...editFiles]);
    }
  };

  return isLoading ? (
    <Backdrop
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={isLoading}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
  ) : (
    <div>
      <Stack
        // style={{ paddingTop: 10 }}
        direction="row"
        spacing={2}
        sx={{
          justifyContent: "flex-end",
          alignItems: "flex-end",
        }}
      >
        <Tooltip title="Upload Comments">
          <IconButton
            color="primary"
            component="label"
            disabled={
              !getUserAccess(
                props.user,
                props.project.uid,
                props.project.Contacts,
              ).allowCreate
            }
            role={undefined}
            variant="contained"
            tabIndex={-1}
          >
            <VisuallyHiddenInput
              type="file"
              ref={fileInputRef}
              onChange={(event) =>
                event.target.files.length > 0 &&
                processExcel(event.target.files[0])
              }
            />
            <FileUploadIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Add Comment">
          <IconButton
            onClick={onAddComment}
            disabled={
              !getUserAccess(
                props.user,
                props.project.uid,
                props.project.Contacts,
              ).allowCreate
            }
            aria-label="Add Comment"
            size="large"
            color="primary"
          >
            <AddBoxIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Stack
        style={{ paddingTop: 30 }}
        sx={{
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {allFeatures.length > 0 && (
          <SortDialog
            sortByValue={sortByValue}
            onSortChange={onSortChange}
            objArray={features}
            sortByOptions={sortByOptions}
            filterValue={filterValue}
            onFilterChange={onFilterChange}
            page={page}
            handleChangePage={handleChangePage}
            rowsPerPage={rowsPerPage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
          />
        )}

        <Collapse
          style={{ width: "100%" }}
          in={features.some((item) => item.Selected === true)}
        >
          <Paper elevation={6} style={{ width: "100%" }}>
            <Box
              sx={{
                width: "100%",
                height: 60,
                borderRadius: 1,
                bgcolor: "secondary.light",
                // "&:hover": {
                //   bgcolor: "primary.dark",
                // },
              }}
            >
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
                style={{ height: "100%", paddingLeft: 16 }}
              >
                <Typography color="primary.contrastText">
                  {`Selected ${
                    features.filter((f) => f.Selected === true).length
                  }`}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onUncheckSelected}
                  startIcon={<Unpublished />}
                >
                  Uncheck All
                </Button>
                {/* <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<Checklist />}
                >
                  Manage Checklist
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<EditDocument />}
                >
                  Manage Files
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<Photo />}
                >
                  Manage Photos
                </Button> */}
                {/* <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<Delete />}
                  onClick={onDeleteComments}
                >
                  {`Delete Selected`}
                </Button> */}
              </Stack>
            </Box>
          </Paper>
        </Collapse>

        {features.length === 0 && filterValue.length <= 2 ? (
          <Typography>
            There aren't any comments associated with this
            {props.equipmentId
              ? " equipment"
              : props.issueId
                ? " issue"
                : props.observationId
                  ? " observation"
                  : "project"}
            . Please
            <Tooltip title={`Upload Comments`}>
              <IconButton
                color="primary"
                component="label"
                disabled={
                  !getUserAccess(
                    props.user,
                    props.project.uid,
                    props.project.Contacts,
                  ).allowCreate
                }
                role={undefined}
                variant="contained"
                tabIndex={-1}
                size="medium"
              >
                <VisuallyHiddenInput
                  type="file"
                  ref={fileInputRef1}
                  onChange={(event) =>
                    event.target.files.length > 0 &&
                    processExcel(event.target.files[0])
                  }
                />
                <FileUploadIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
            upload or add
            <Tooltip title="Add Comment">
              <IconButton
                disabled={
                  !getUserAccess(
                    props.user,
                    props.project.uid,
                    props.project.Contacts,
                  ).allowCreate
                }
                onClick={onAddComment}
                aria-label="Add Comment"
                size="medium"
                color="primary"
              >
                <AddBoxIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
            new Comment
          </Typography>
        ) : features.length === 0 && filterValue.length > 2 ? (
          <Typography>
            There aren't any comments associated with your search: "
            <strong>{filterValue}</strong>".
          </Typography>
        ) : (
          <List style={{ width: "100%" }}>
            <CommentCard
              features={features}
              page={page}
              rowsPerPage={rowsPerPage}
              onEdit={onEdit}
              allowUpdate={
                getUserAccess(props.user, project.uid, project.Contacts)
                  .allowUpdate
              }
              onDetailsClick={onDetailsClick}
              handleChecked={handleChecked}
            />
          </List>
        )}
      </Stack>
      <ImportDialog
        isOpen={importDialogOpen}
        onClose={onCloseImportDialog}
        importTitle={importTitle}
        importSubtitle={importSubtitle}
        importData={importData}
        importFields={importFields}
        onFldChange={onFldChange}
        onAttributeChange={onAttributeChange}
        onImportExcel={onImportExcel}
        importStats={importStats}
        sheetName={sheetName}
        workbook={workbook}
        onWorksheetSelected={onWorksheetSelected}
      />

      <EditDialog
        allowDeleteAccess={
          getUserAccess(props.user, project.uid, project.Contacts).allowDelete
        }
        feature={editFeature}
        editFiles={editFiles}
        importTitle={importTitle}
        getAcceptedFiles={getAcceptedFiles}
        onEditFileCommentChange={onEditFileCommentChange}
        onRemoveEditFile={onRemoveEditFile}
        schema={editSchema}
        onChange={onEditChange}
        onEditSave={onEditSave}
        onEditDelete={onEditDelete}
        isOpen={editDialogOpen}
        onClose={onEditCancel}
        formErrorMessage={formErrorMessage}
        allowDelete={true}
        allowFiles={true}
        onDrawComplete={onDrawComplete}
      />
    </div>
  );
}
